import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { validateChildTable } from "@/components/child-table/utils/validation";
import { validateRowLinks } from "@/components/child-table/utils/linkValidation";
import { getVisibleChildTableFields } from "@/components/child-table/utils/schemaUtils";
import type { RowValidationError } from "@/components/child-table/types";
import type { DocTypeSchema } from "@/lib/erp/meta";

/**
 * Hook that provides form validation for Item documents.
 *
 * Validates child tables (required fields + link references) using
 * cached or fetched schemas. Fails closed — if schema cannot be
 * loaded, validation fails rather than silently skipping.
 */
export function useItemValidation(
  metaSchema: DocTypeSchema | undefined,
  formData: Record<string, any>,
) {
  const queryClient = useQueryClient();

  const validateForm = useCallback(
    async (
      dataToValidate: Record<string, any> = formData,
    ): Promise<{ valid: boolean; errors: string[] }> => {
      if (!metaSchema) {
        return {
          valid: false,
          errors: [
            "Cannot validate Item: schema is not loaded. Please refresh and ensure your user has permission to read DocType metadata.",
          ],
        };
      }

      const errors: string[] = [];

      // Find all Table fields from metadata
      const tableFields = metaSchema.fields.filter(
        (field) => field.fieldtype === "Table" && field.options,
      );

      for (const field of tableFields) {
        const tableData = dataToValidate[field.fieldname];

        if (Array.isArray(tableData) && tableData.length > 0) {
          // Check cache first to avoid unnecessary network calls
          let childSchema: DocTypeSchema | null | undefined =
            queryClient.getQueryData<DocTypeSchema>(["meta", field.options]);

          // If not cached, try to fetch schema
          // NO COMPROMISES: We must validate or fail closed — no silent skips
          if (!childSchema) {
            try {
              childSchema = await queryClient.fetchQuery<DocTypeSchema>({
                queryKey: ["meta", field.options],
                queryFn: async () => {
                  return await erp.meta.getDocTypeSchema(field.options!);
                },
                retry: false,
              });
            } catch (schemaError) {
              const errorMsg =
                schemaError instanceof Error
                  ? schemaError.message
                  : String(schemaError);
              const isPermissionError = /403|forbidden|permission/i.test(
                errorMsg,
              );

              errors.push(
                `Cannot validate ${field.label || field.fieldname} child table: ${
                  isPermissionError
                    ? "Permission denied. Ensure your user can read metadata (ERPNext role: System Manager or Item Manager)."
                    : `Failed to fetch schema: ${errorMsg}`
                }`,
              );
              continue;
            }
          }

          if (
            !childSchema ||
            !childSchema.fields ||
            childSchema.fields.length === 0
          ) {
            errors.push(
              `Cannot validate ${field.label || field.fieldname} child table: Schema has no fields available.`,
            );
            continue;
          }

          const visibleFields = getVisibleChildTableFields(childSchema);

          // Required field validation (synchronous)
          const requiredErrors = validateChildTable(
            tableData,
            visibleFields,
            field.label,
          );
          errors.push(...requiredErrors.map((e) => e.message));

          // Link validation (async — parallelized, no N+1)
          const linkValidationPromises = tableData
            .map((row, i) => {
              if (row.__deleted) return null;
              return validateRowLinks(row, i, visibleFields);
            })
            .filter(
              (p): p is Promise<RowValidationError[]> => p !== null,
            );

          const linkValidationResults = await Promise.all(
            linkValidationPromises,
          );
          for (const linkErrors of linkValidationResults) {
            errors.push(...linkErrors.map((e) => e.message));
          }
        }
      }

      return { valid: errors.length === 0, errors };
    },
    [formData, metaSchema, queryClient],
  );

  return validateForm;
}
